import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { VIS_NEUTRAL_COLOR } from 'visyn_core/vis';

import { sortByMostOccurrences } from '../analysis/utils';
import { LIABILITY_SEVERITY } from '../antibody-analysis/utilities/liabilityBadge';
import { colorMap, getClonotypeIdColor, getShmCategoryColor } from '../colors';
import { CellResponseWithClonotype, NodeResponse } from '../generated/generatedOpenAPI';
import { ColorMapBy } from '../store/slices/clonotypes.slice';

export interface NetworkMapClonotypeNode {
  id: string;
  cdr3Sequence: string;
  x: number;
  y: number;
  cells: CellResponseWithClonotype[];
  liabilityStatus: string | null;
  shmCategory: string | null;
}

const LIABILITY_SEVERITY_BY_STATUS: { [status: string]: (typeof LIABILITY_SEVERITY)[number] } = Object.fromEntries(
  LIABILITY_SEVERITY.flatMap((severity) => severity.statuses.map((status) => [status, severity])),
);

// #D4D7D8 = the vis unselected colour without opacity. Shared so the
// (ClonotypePlot) and the static SVG mini-graph (MiniNetworkGraphSvg) grey out
// unselected clonotypes identically. Same code that earlier network graph was using.Keeping it DRY
export const VIS_UNSELECTED_COLOR = '#D4D7D8';

/**
 * Maps a node's cell count to a node RADIUS in pixels.
 *
 * Shared between both renderers( Clonotype and MiniNetworkGraph) so they never drift.
 */
export function getNodeSize(size: number): number {
  if (size === 1) {
    return 3;
  }
  if (size > 1 && size < 5) {
    return 5;
  }
  if (size > 4 && size < 11) {
    return 7;
  }
  if (size > 10 && size < 20) {
    return 9;
  }
  if (size > 19) {
    return 11;
  }
  return 0;
}
type ColorableNode = Pick<NetworkMapClonotypeNode, 'cells'> & Partial<Pick<NetworkMapClonotypeNode, 'liabilityStatus' | 'shmCategory'>>;

/** Fill colour for a node under the current "Colour by" mode. */
export function getNodeColor(node: ColorableNode, colorMapBy: ColorMapBy | null, clonotypeId: number): string {
  const cell = node.cells[0];
  if (colorMapBy === ColorMapBy.CLONOTYPE_ID) {
    return getClonotypeIdColor(clonotypeId);
  }
  if (colorMapBy === ColorMapBy.LG_ISO_TYPE) {
    return (cell?.irVdj1CCall ? colorMap[cell.irVdj1CCall] : undefined) ?? VIS_NEUTRAL_COLOR;
  }
  if (colorMapBy === ColorMapBy.LEIDEN_CLUSTER) {
    return (cell?.leiden ? colorMap[cell.leiden] : undefined) ?? VIS_NEUTRAL_COLOR;
  }
  if (colorMapBy === ColorMapBy.LIABILITY) {
    return (node.liabilityStatus ? LIABILITY_SEVERITY_BY_STATUS[node.liabilityStatus]?.color : undefined) ?? VIS_NEUTRAL_COLOR;
  }
  if (colorMapBy === ColorMapBy.SHM) {
    return getShmCategoryColor(node.shmCategory) ?? VIS_NEUTRAL_COLOR;
  }
  return VIS_NEUTRAL_COLOR;
}

/** Tooltip legend label describing the node's colour under the current "Colour by" mode. */
export function getNodeLegendLabel(node: ColorableNode, colorMapBy: ColorMapBy | null, clonotypeId: number): string | null {
  if (colorMapBy === ColorMapBy.LG_ISO_TYPE) {
    return `Ig isotype: ${node.cells[0]?.irVdj1CCall ?? 'N/A'}`;
  }
  if (colorMapBy === ColorMapBy.LEIDEN_CLUSTER) {
    return `Leiden cluster: ${node.cells[0]?.leiden ?? 'N/A'}`;
  }
  if (colorMapBy === ColorMapBy.CLONOTYPE_ID) {
    return `Clonotype ID: ${clonotypeId}`;
  }
  if (colorMapBy === ColorMapBy.LIABILITY) {
    return `Liability: ${(node.liabilityStatus ? LIABILITY_SEVERITY_BY_STATUS[node.liabilityStatus]?.label : undefined) ?? 'N/A'}`;
  }
  if (colorMapBy === ColorMapBy.SHM) {
    return `SHM: ${node.shmCategory ?? 'N/A'}`;
  }
  return null;
}

export type NetworkMapClonotype = {
  clonotypeId: number;
  clonotypeSize: number;
  irVdj1CCall: string | null;
  nodes: NetworkMapClonotypeNode[];
  links: Array<{
    source: string;
    target: string;
  }>;
};

export function transformNodesToNetworkMapClonotypes(nodes: NodeResponse[], cells: CellResponseWithClonotype[]): NetworkMapClonotype[] {
  if (!nodes || nodes.length === 0) {
    return [];
  }
  const keyedCells = keyBy(cells, 'cellId');

  const cellsGroupedByClonotypeId = groupBy(cells, 'clonotypeId');

  const filteredNodes = nodes.filter((node) => {
    const cellsForClonotypeId = cellsGroupedByClonotypeId[node.clonotypeId];
    if (!cellsForClonotypeId) {
      return false;
    }
    return cellsForClonotypeId.length > 0;
  });

  const groupedByClonotype = groupBy(filteredNodes, 'clonotypeId');
  return map(groupedByClonotype, (clonotypeNodes: NodeResponse[], clonotypeId: string): NetworkMapClonotype => {
    const clonotypeIdNumber = parseInt(clonotypeId, 10);
    const currentClonotypeNodes: NetworkMapClonotypeNode[] = clonotypeNodes
      .map((nodeResponse) => {
        return {
          id: String(nodeResponse.nodeId),
          cdr3Sequence: nodeResponse.cdr3Sequence,
          x: nodeResponse.x,
          y: nodeResponse.y,
          liabilityStatus: nodeResponse.liabilityStatus ?? null,
          shmCategory: nodeResponse.shmCategory ?? null,
          cells: nodeResponse.cellIds?.map((cellId) => keyedCells[cellId]).filter((cell): cell is CellResponseWithClonotype => !!cell) || [],
        };
      })
      .filter((node) => node.cells.length > 0);

    const links: Array<{ source: string; target: string }> = flatMap(clonotypeNodes, (nodeResponse) => {
      return nodeResponse.links.map((targetId) => ({
        source: String(nodeResponse.nodeId),
        target: String(targetId),
      }));
    }).filter((link, index, self) => index === self.findIndex((l) => l.source === link.source && l.target === link.target));

    const clonotypeSize = currentClonotypeNodes[0]?.cells[0]?.clonotypeSize || 0;

    const irVdj1CCallArray = Array.from(
      new Set(
        sortByMostOccurrences(
          cellsGroupedByClonotypeId[clonotypeIdNumber]?.map((cell) => cell.irVdj1CCall).filter((v): v is string => typeof v === 'string') ?? [],
        ),
      ),
    );

    return {
      clonotypeId: clonotypeIdNumber,
      nodes: currentClonotypeNodes,
      links,
      clonotypeSize,
      irVdj1CCall: irVdj1CCallArray.length > 0 && irVdj1CCallArray[0] ? irVdj1CCallArray[0] : null,
    };
  });
}
